import {
    ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal
} from "@angular/core";
import {
    ActivatedRoute
} from "@angular/router";
import {
    ObjectUtil
} from "@shopliftr/common-js/data";
import { MgdRouterService } from "../../services/mgd-router.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationSource } from "../../enums/navigation-source";
import { IBreadcrumb } from "./interface/breadcrumb";
import { MatIcon } from "@angular/material/icon";
import { AsyncPipe } from "@angular/common";
import { MatButton } from "@angular/material/button";
import { ButtonDirective } from "../../directives/button/button.directive";
import { COMMON_APP_SERVICE } from "../../constants/app-tokens";

/**
 * Breadcrumbs that populate based on the breadcrumb field in the route data
 *
 * The breadcrumbs will automatically disable when the app is processing
 */
@Component({
    selector: "mgd-router-breadcrumbs",
    templateUrl: "./router-breadcrumbs.component.html",
    styleUrls: ["./router-breadcrumbs.component.scss"],
    imports: [MatIcon, AsyncPipe, MatButton, ButtonDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RouterBreadcrumbsComponent implements OnInit {

    /**
     * The breadcrumbs currently displayed, built from the route data
     */
    readonly breadcrumbs = signal<Array<IBreadcrumb>>([]);

    /**
     * Used to determine if the app is processing
     */
    readonly appState = inject(COMMON_APP_SERVICE);

    /**
     * The reference for when this component is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Used to populate the breadcrumbs and navigate
     */
    private readonly _mgdRouter = inject(MgdRouterService);

    ngOnInit(): void {

        this._mgdRouter.routeChange$.pipe(
            // Unsubscribe when the component is destroyed
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((route) => {
            if (route) {
                // Build breadcrumbs when the route changes
                this._buildBreadcrumbs(route.route);
            }
        });
    }

    /**
     * Click handler for each breadcrumb
     *
     * @param crumb The crumb that was clicked
     */
    onClick(crumb: IBreadcrumb): void {

        if (crumb?.route) {

            this._mgdRouter.navigateToRoute(crumb.route, NavigationSource.Breadcrumb);
        }
    }

    /**
     * Builds the breadcrumbs based on the route data
     *
     * @param route The current route
     */
    private _buildBreadcrumbs(route: ActivatedRoute): void {

        const newCrumbs = new Array<IBreadcrumb>();
        let lastBreadcrumb: string;

        route.pathFromRoot.forEach((currentRoute) => {
            const breadcrumb: string = currentRoute.snapshot.data?.breadcrumb;
            if (breadcrumb) {
                const newCrumb = {
                    value: breadcrumb,
                    route: currentRoute,
                    last: currentRoute === route
                };
                if (lastBreadcrumb === breadcrumb) {
                    // For repeated breadcrumbs keep the first route in the run so the crumb navigates to the
                    // outermost route, and stay disabled (last) if any route in the run is the current page
                    const previousCrumb = newCrumbs[newCrumbs.length - 1];
                    newCrumbs[newCrumbs.length - 1] = {
                        ...previousCrumb,
                        last: previousCrumb.last || newCrumb.last
                    };
                }
                else {
                    newCrumbs.push(newCrumb);
                }
                lastBreadcrumb = breadcrumb;
            }
        });

        if (!ObjectUtil.equals(this.breadcrumbs(), newCrumbs)) {
            this.breadcrumbs.set(newCrumbs);
        }
    }
}
